import { useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '../../store/store';
import { authActions } from '../../store/slices/auth';
import { authError, rateLimitSeconds } from './erros';

// A CONFIRMAÇÃO DO E-MAIL POR CÓDIGO (OTP) — a lógica do antigo `EmailCodeStep`, sem o desenho.
// Serve ao cadastro e ao login (quem volta com o e-mail ainda não confirmado). `reenviarAoAbrir`
// dispara um código novo ao montar (o do cadastro pode ter expirado).
// Mora no núcleo desde 01/11/2026 (docs/PLANO-APP-NATIVO.md): a web e o app usam a mesma regra.

export const TAMANHO_DO_CODIGO = 6;
const ESPERA_PARA_REENVIAR = 45; // segundos entre reenvios (evita o rate limit de e-mail)

export const useCodigoDoEmail = (email: string, aoConfirmar: () => void, reenviarAoAbrir?: boolean) => {
  const dispatch = useAppDispatch();
  const [codigo, setCodigoBruto] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [espera, setEspera] = useState(0);

  const comecarEspera = (segundos = ESPERA_PARA_REENVIAR) => setEspera(segundos);

  /**
   * A falha de um envio. Quando é rate limit, o servidor diz de quantos segundos é a espera: o
   * número vai na mensagem e no contador. Sem contador, o botão continuava dizendo "Reenviar
   * código", e cada nova tentativa renovava o bloqueio sem a pessoa entender por quê.
   */
  const aoFalharEnvio = (err: any) => {
    const s = rateLimitSeconds(err);
    if (s != null) {
      comecarEspera(s);
      setError(s === 1 ? 'Aguarde 1 segundo para pedir um novo código.' : `Aguarde ${s} segundos para pedir um novo código.`);
      return;
    }
    setError(authError(err));
  };

  useEffect(() => {
    if (espera <= 0) return undefined;
    const t = setTimeout(() => setEspera((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const enviouAoAbrir = useRef(false);
  useEffect(() => {
    if (!reenviarAoAbrir || enviouAoAbrir.current) return;
    enviouAoAbrir.current = true;
    dispatch(authActions.resendSignupOtp({ email }))
      .unwrap()
      .then(() => { setInfo('Enviamos um código para o seu e-mail.'); comecarEspera(); })
      .catch(aoFalharEnvio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guarda SÍNCRONO: o envio automático (código completo) e o clique podiam disparar duas
  // verificações quase juntas; cada uma gasta uma tentativa e a segunda batia no código já usado.
  const verificando = useRef(false);
  const verificar = async (token: string) => {
    if (token.length < TAMANHO_DO_CODIGO || verificando.current) return;
    verificando.current = true;
    setLoading(true);
    setError(null);
    try {
      await dispatch(authActions.verifySignupOtp({ email, token })).unwrap();
      aoConfirmar();
    } catch (err: any) {
      // Aqui o limite é de TENTATIVAS, e não de envio.
      const s = rateLimitSeconds(err);
      if (s != null) {
        comecarEspera(s);
        setError(`Muitas tentativas. Aguarde ${s} ${s === 1 ? 'segundo' : 'segundos'}.`);
      } else {
        setError(authError(err) || 'Código inválido ou expirado.');
      }
      setLoading(false);
      verificando.current = false;
    }
  };

  // Só dígitos, no máximo seis; com o sexto, confirma sozinho (também ao colar o código inteiro).
  const setCodigo = (valor: string) => {
    const digitos = valor.replace(/\D/g, '').slice(0, TAMANHO_DO_CODIGO);
    setCodigoBruto(digitos);
    if (digitos.length === TAMANHO_DO_CODIGO) void verificar(digitos);
  };

  const reenviar = async () => {
    if (espera > 0) return;
    setError(null);
    setInfo(null);
    try {
      await dispatch(authActions.resendSignupOtp({ email })).unwrap();
      setInfo('Enviamos um novo código para o seu e-mail.');
      comecarEspera();
    } catch (err: any) {
      aoFalharEnvio(err);
    }
  };

  return { codigo, setCodigo, loading, error, info, espera, confirmar: () => verificar(codigo), reenviar };
};
